/**
 * Component to generate the calendar matrix
 *
 * @author bgr, che
 */

import { addDays, addMonth, compareDates } from "../../shared/l10n/date";

/**
 * Get calendar days by month, week and day in the week (using locale specific first day of the week),
 * in the following format:
 *
 * [
 *   {
 *     year: {integer},
 *     month: {integer},
 *     weeks: [
 *       [
 *         {
 *           date: {Date},
 *           isToday: {bool}
 *           isCurrentMonth: {bool},
 *           isPast: {bool}
 *         },
 *         ...
 *       ],
 *       ...
 *     ]
 *   },
 *   ...
 * ]
 *
 * @param {Date} today - Date to start the calendar with
 * @param {Number} numberOfMonths - Number of months to be build
 * @param {Number} firstDayOfWeek optional default is 0 for Sunday
 * @param {Date} earliestDay optional earliest date in the calendar, defaults to today
 * @return {array}
 */
export function build(
	today,
	numberOfMonths,
	firstDayOfWeek = 0,
	earliestDay = null,
) {
	const calendarMonths = [];

	earliestDay = earliestDay || today;
	let firstDayOfMonth = new Date(
		earliestDay.getFullYear(),
		earliestDay.getMonth(),
		1,
	);

	for (let monthCounter = 0; monthCounter < numberOfMonths; monthCounter++) {
		const calendarMonth = {
			year: firstDayOfMonth.getFullYear(),
			month: firstDayOfMonth.getMonth(),
			weeks: [],
		};

		// Calculate days in first week before first of month
		let previousDays = firstDayOfMonth.getDay() - firstDayOfWeek;
		if (previousDays < 0) {
			previousDays += 7;
		}

		// First day in this month for the calendar
		let currentDay = addDays(firstDayOfMonth, -previousDays);

		// Number of weeks shown for this month in the calendar
		const numberOfWeeks = Math.ceil(
			(getDaysInMonth(firstDayOfMonth) + previousDays) / 7,
		);

		for (let weekCounter = 0; weekCounter < numberOfWeeks; weekCounter++) {
			const week = [];

			for (let dayCounter = 0; dayCounter < 7; dayCounter++) {
				// Today is always in the first month (can be repeated in second), other months are always in future
				const compareToday =
					monthCounter > 1 ? 1 : compareDates(currentDay, today);
				const day = {
					date: currentDay,
					isToday: compareToday === 0,
					isCurrentMonth:
						firstDayOfMonth.getFullYear() === currentDay.getFullYear() &&
						firstDayOfMonth.getMonth() === currentDay.getMonth(),
					isPast: compareToday === -1,
					isFutureBeyond:
						monthCounter === numberOfMonths - 1 &&
						currentDay.getMonth() > firstDayOfMonth.getMonth(),
				};

				week.push(day);

				// Prepare next day
				currentDay = addDays(currentDay, 1);
			}

			calendarMonth.weeks.push(week);
		}

		calendarMonths.push(calendarMonth);

		// Prepare next month
		firstDayOfMonth = addMonth(firstDayOfMonth, 1);
	}

	return calendarMonths;
}

/**
 * @param {Date} date
 * @returns {Number}
 */
const getDaysInMonth = date => {
	const month = date.getMonth();
	const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
	if (month === 1 && isLeapYear(date)) {
		return 29;
	}
	return days[month]; // Number
};

/**
 * @param {Date} date
 * @returns {Boolean}
 */
const isLeapYear = date => {
	const year = date.getFullYear();
	return !(year % 400) || (!(year % 4) && !!(year % 100));
};
